import type { PointerEvent as ReactPointerEvent } from 'react';
import type { SortDescriptor } from 'react-aria-components';
import { Column, ColumnResizer } from 'react-aria-components';
import { Icon } from '@launchpad-ui/icons';
import clsx from 'clsx';

import { DataTableColumnHeaderActions } from './DataTableColumnHeaderActions';
import type { ColumnWidth, DataTableContainerStyle, DataTableInlineEdge } from './types';
import type { ColumnDropPosition } from './useColumnReordering';
import type { ResolvedColumn } from './useDataTableColumns';

import styles from './DataTable.module.css';

const SORT_ICONS = { ascending: 'arrow-up-thin', descending: 'arrow-down-thin' } as const;

function toHexColor(color: string | undefined) {
  if (!color) {
    return undefined;
  }

  const hex = color.startsWith('#') ? color : `#${color}`;
  return /^#[0-9a-fA-F]{3,8}$/.test(hex) ? hex : undefined;
}

type Props<T> = {
  column: ResolvedColumn<T>;
  defaultColumnActionsEnabled: boolean;
  isResizable: boolean;
  widthOverride?: ColumnWidth;
  defaultWidthOverride?: ColumnWidth;
  stickyIndex?: number;
  stickyOffset?: string;
  stickyEndIndex?: number;
  stickyEndOffset?: string;
  stickyEdge?: 'start' | 'end';
  inlineEdge?: DataTableInlineEdge;
  isDragging: boolean;
  dropPosition?: ColumnDropPosition;
  onHeaderPointerDown: (event: ReactPointerEvent<HTMLElement>) => void;
  sortDescriptor?: SortDescriptor;
  canHide: boolean;
  isHideDisabled: boolean;
  canRemoveSort: boolean;
  onSort: (direction?: 'ascending' | 'descending') => void;
  onHide: () => void;
  onResetSize: () => void;
  canMoveLeft: boolean;
  canMoveRight: boolean;
  onMoveLeft: () => void;
  onMoveRight: () => void;
  stackOrder: number;
};

export function DataTableColumnHeader<T>({
  column,
  defaultColumnActionsEnabled,
  isResizable,
  widthOverride,
  defaultWidthOverride,
  stickyIndex,
  stickyOffset,
  stickyEndIndex,
  stickyEndOffset,
  stickyEdge,
  inlineEdge,
  isDragging,
  dropPosition,
  onHeaderPointerDown,
  sortDescriptor,
  canHide,
  isHideDisabled,
  canRemoveSort,
  onSort,
  onHide,
  onResetSize,
  canMoveLeft,
  canMoveRight,
  onMoveLeft,
  onMoveRight,
  stackOrder,
}: Props<T>) {
  const canResize = isResizable && column.isResizable !== false;
  const canReorder = column.isReorderable !== false;
  const isEnvironmentHeader = column.headerStyle === 'environment';
  const environmentColor = toHexColor(column.environmentColor);
  const environmentMarkerStyle: DataTableContainerStyle | undefined = environmentColor
    ? { '--environment-color': environmentColor }
    : undefined;

  return (
    <Column
      id={column.id}
      isRowHeader={column.isRowHeader}
      allowsSorting={column.allowsSorting}
      width={widthOverride ?? column.width}
      defaultWidth={defaultWidthOverride ?? column.defaultWidth}
      minWidth={column.minWidth}
      maxWidth={column.maxWidth}
      textValue={column.name}
      className={clsx(styles.column, {
        [styles.columnEnd]: column.align === 'end',
        [styles.stickyColumn]: stickyIndex !== undefined || stickyEndIndex !== undefined,
      })}
      data-header-style={isEnvironmentHeader ? 'environment' : undefined}
      data-dragging={isDragging || undefined}
      data-drop-position={dropPosition}
      data-sticky-column-index={stickyIndex}
      data-sticky-end-index={stickyEndIndex}
      data-sticky-column-edge={stickyEdge}
      data-inline-edge={inlineEdge}
      style={{ zIndex: stackOrder, insetInlineStart: stickyOffset, insetInlineEnd: stickyEndOffset }}
    >
      {({ sortDirection }) => (
        <>
          <div className={styles.headerInner} onPointerDown={canReorder ? onHeaderPointerDown : undefined}>
            <div className={styles.headerContent}>
              {column.renderHeader ? (
                column.renderHeader()
              ) : (
                <span
                  className={clsx(styles.headerName, isEnvironmentHeader && styles.environmentName)}
                  title={column.name}
                >
                  {isEnvironmentHeader && (
                    <span className={styles.environmentMarker} role="presentation" style={environmentMarkerStyle} />
                  )}
                  {isEnvironmentHeader ? <span className={styles.headerLabel}>{column.name}</span> : column.name}
                </span>
              )}
              {sortDirection && <Icon className={styles.sortIcon} name={SORT_ICONS[sortDirection]} size="small" />}
            </div>
            {(defaultColumnActionsEnabled ? column.headerActions !== false : Boolean(column.headerActions)) && (
              <DataTableColumnHeaderActions
                column={column}
                defaultColumnActionsEnabled={defaultColumnActionsEnabled}
                sortDescriptor={sortDescriptor}
                canHide={canHide}
                isHideDisabled={isHideDisabled}
                canRemoveSort={canRemoveSort}
                canResize={canResize}
                canMoveLeft={canReorder && canMoveLeft}
                canMoveRight={canReorder && canMoveRight}
                onSort={onSort}
                onHide={onHide}
                onResetSize={onResetSize}
                onMoveLeft={onMoveLeft}
                onMoveRight={onMoveRight}
              />
            )}
          </div>
          {canResize && <ColumnResizer className={styles.resizer} data-column-resizer />}
        </>
      )}
    </Column>
  );
}
